import {
	ConfigLayer,
	tilesConfigCreator,
	tilesConfigCreatorV3,
	tilesJsonConfigCreator,
	amSpatialConfigCreatorV3,
} from '@innovyze/inno-map';
import { LineStringType, PointType, PolygonType } from '@innovyze/shared-utils';
import {
	MapOptions,
	MapRehabLayer,
	MapRiskLayer,
	MapType,
	SourceOptions,
	SpatialLayer,
} from '@Types';
import {
	inspectionCctv,
	inspectionCctvObs,
	inspectionManhole,
	inspectionsLayerName,
	ratingColours,
	rehabColors,
	riskGradeColours,
	riskGradeLayerNames,
} from './index';
import { getTilesBaseUrl, tilesBaseUrl } from '@Apis';
import { getSystemTypeIdByAssetTypePrefix } from '@innovyze/lib_asset_schema';
import {
	assetSchemaSelectorByType,
	getAssetSchemaAction,
} from '@innovyze/lib_asset_schema_store';
import store from '@Store/index';

const debugRiskValues = false;
const riskValues: number[] = debugRiskValues
	? [-1, 0, 1, 2, 3, 4, 5]
	: [0, 1, 2, 3, 4, 5];

// This function just to dance around the TS array/tuple madness
// of LSP that gets confused between [number, number] and number[]
const propertyLevel = (key: string, level: number) => {
	const p: Record<string, [number, number]> = {};
	p[key] = [level - 0.5, level + 0.5];
	return p;
};

function createTileSourceRegExp(assetType: string): RegExp {
	const systemType = getSystemTypeIdByAssetTypePrefix(assetType);

	return new RegExp(`^${systemType}(-${assetType})?$`);
}

const riskLayerGroup = (
	opts: SourceOptions | undefined,
	name: string,
	zIndex: number,
	groupCount: number,
	groupIndex: number,
	assetType = 'wwPipe',
): ConfigLayer => {
	const isManhole = assetType.toLowerCase().includes('manhole');
	const layers: ConfigLayer[] = isManhole
		? [0, 1, 2, 3, 4].map(n => ({
				layerName: riskGradeLayerNames[n],
				type: 'symbol',
				icon: 'manhole-survey',
				color: riskGradeColours[n],
				properties: propertyLevel(`risk${groupIndex}`, n),
				selectable: false,
		  }))
		: [0, 1, 2, 3, 4].map(n => ({
				layerName: riskGradeLayerNames[n],
				type: 'line',
				color: riskGradeColours[n],
				properties: propertyLevel(`risk${groupIndex}`, n),
				selectable: false,
		  }));

	return {
		layerName: name,
		dataServiceId: opts?.dataServiceId ?? '',
		tileSource: createTileSourceRegExp(assetType),
		tileLayerName: assetType,
		zIndex: zIndex + groupCount - groupIndex, // highest priority first
		layers: layers,
	};
};

const getGeometryType = (assetType: string) => {
	store.dispatch(getAssetSchemaAction({ assetType: assetType ?? '' }));

	const state = store.getState();
	const assetSchema = assetSchemaSelectorByType(state, assetType ?? '');

	return assetSchema?.geometryType;
};

const getLayerConfigFromGeometryType = (
	geometryType: string | undefined,
): {
	type: 'symbol' | 'polygon' | 'line';
	icon?: string;
} => {
	switch (geometryType) {
		case 'Point':
			return {
				type: 'symbol',
				icon: 'manhole-survey',
			};
		case 'Polygon':
			return {
				type: 'polygon',
			};
		default:
			return {
				type: 'line',
			};
	}
};

const riskTilesLayerGroup = (
	name: string,
	zIndex: number,
	groupCount: number,
	groupIndex: number,
	configId: string,
	assetType = 'wwPipe',
) => {
	const geometryType = getGeometryType(assetType);
	const layerConfig = getLayerConfigFromGeometryType(geometryType);

	const layers: ConfigLayer[] = [0, 1, 2, 3, 4].map(n => ({
		layerName: riskGradeLayerNames[n],
		id: `risk${configId}-${n}`,
		dataServiceId: `Risk/${configId}`,
		...layerConfig,
		color: riskGradeColours[n],
		properties: {
			riskGrade: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

	const layer = {
		layerName: name,
		tileSource: `Risk/${configId}`,
		tileLayerName: configId.replaceAll('-', ''),
		zIndex: zIndex + groupCount - groupIndex, // highest priority first
		layers: layers,
	};

	return layer;
};

// V3 risk layer helper - uses 'tiles' dataServiceId and 'Risk' tileSource
const v3RiskLayerGroup = (
	name: string,
	zIndex: number,
	groupCount: number,
	groupIndex: number,
	configId: string,
	assetType = 'wwPipe',
) => {
	const geometryType = getGeometryType(assetType);
	const layerConfig = getLayerConfigFromGeometryType(geometryType);

	const layers: ConfigLayer[] = [0, 1, 2, 3, 4].map(n => ({
		layerName: riskGradeLayerNames[n],
		id: `risk${configId}-${n}`,
		dataServiceId: 'tiles',
		...layerConfig,
		color: riskGradeColours[n],
		properties: {
			riskGrade: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

	const layer = {
		layerName: name,
		tileSource: 'Risk',
		tileLayerName: configId.replaceAll('-', ''),
		zIndex: zIndex + groupCount - groupIndex,
		layers: layers,
	};

	return layer;
};

export const riskLayers = (
	opts: SourceOptions | undefined,
	riskLayers: MapRiskLayer[] = [],
	isTiledRiskEnabled = false,
	isTilesV3Enabled = false,
): ConfigLayer[] => {
	if (riskLayers.length < 1) {
		return [];
	}

	const zIndexLevelRisk = 10;

	// V3: Use Risk system from V3 tileset
	if (isTilesV3Enabled) {
		return [
			{
				layerName: 'Risk levels',
				dataServiceId: 'tiles',
				layers: riskLayers.map((l, n) => {
					return v3RiskLayerGroup(
						l.name,
						zIndexLevelRisk,
						riskLayers.length,
						n,
						l.configId ?? '',
						l.assetType,
					);
				}),
			},
		];
	}

	if (isTiledRiskEnabled) {
		return [
			{
				layerName: 'Risk levels',
				dataServiceId: 'tiled-risk',
				layers: riskLayers.map((l, n) => {
					return riskTilesLayerGroup(
						l.name,
						zIndexLevelRisk,
						riskLayers.length,
						n,
						l.configId ?? '',
						l.assetType,
					);
				}),
			},
		];
	} else {
		return [
			{
				layerName: 'Risk levels',
				layers: riskLayers.map((l, n) =>
					riskLayerGroup(
						opts,
						l.name,
						zIndexLevelRisk,
						riskLayers.length,
						n,
						l.assetType,
					),
				),
			},
		];
	}
};

const cctvLayers = (
	opts: SourceOptions | undefined,
	noMinZoom = false,
): ConfigLayer[] =>
	riskValues.map(n => ({
		layerName: `${n}`,
		id: `cctv${n}`,
		dataServiceId: opts?.dataServiceId ?? '',
		type: 'line',
		color: ratingColours[n],
		lineWidth: 5,
		zIndex: 1000 + n,
		minZoom: noMinZoom ? 0 : opts?.minZoom ?? 0,
		properties: {
			rating: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

const tiledCctvLayers = (): ConfigLayer[] =>
	riskValues.map(n => ({
		layerName: `${n}`,
		id: `cctv${n}`,
		dataServiceId: 'tiled-inspections',
		type: 'line',
		color: ratingColours[n],
		lineWidth: 5,
		zIndex: 1000 + n,
		properties: {
			rating: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

const observationLayers = (
	opts: SourceOptions | undefined,
	noMinZoom = false,
): ConfigLayer[] =>
	riskValues.map(n => ({
		layerName: `${n}`,
		id: `obs${n}`,
		dataServiceId: opts?.dataServiceId ?? '',
		type: 'symbol',
		icon: 'defect',
		color: ratingColours[n],
		labelColor: ratingColours[n],
		zIndex: 1100 + n,
		minZoom: noMinZoom ? 0 : opts?.minZoom ?? 0,
		properties: {
			rating: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

const tiledObservationLayers = (): ConfigLayer[] =>
	riskValues.map(n => ({
		layerName: `${n}`,
		id: `obs${n}`,
		dataServiceId: 'tiled-inspections',
		type: 'symbol',
		icon: 'defect',
		color: ratingColours[n],
		labelColor: ratingColours[n],
		zIndex: 1100 + n,
		properties: {
			rating: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

const manholeSurveyLayers = (
	opts: SourceOptions | undefined,
	noMinZoom = false,
): ConfigLayer[] =>
	riskValues.map(n => ({
		layerName: `${n}`,
		id: `manhole-survey-${n}`,
		dataServiceId: opts?.dataServiceId,
		type: 'symbol',
		icon: 'manhole-survey',
		color: ratingColours[n],
		lineWidth: 5,
		zIndex: 1000 + n,
		minZoom: noMinZoom ? 0 : opts?.minZoom ?? 0,
		properties: {
			rating: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

const tiledManholeSurveyLayers = (): ConfigLayer[] =>
	riskValues.map(n => ({
		layerName: `${n}`,
		id: `manhole-survey-${n}`,
		dataServiceId: 'tiled-inspections',
		type: 'symbol',
		icon: 'manhole-survey',
		color: ratingColours[n],
		lineWidth: 5,
		zIndex: 1000 + n,
		properties: {
			rating: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

// V3 inspection layer helpers - use 'tiles' dataServiceId and 'Inspections' tileSource
const v3CctvLayers = (): ConfigLayer[] =>
	riskValues.map(n => ({
		layerName: `${n}`,
		id: `cctv${n}`,
		dataServiceId: 'tiles',
		type: 'line',
		color: ratingColours[n],
		lineWidth: 5,
		zIndex: 1000 + n,
		properties: {
			rating: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

const v3ObservationLayers = (): ConfigLayer[] =>
	riskValues.map(n => ({
		layerName: `${n}`,
		id: `obs${n}`,
		dataServiceId: 'tiles',
		type: 'symbol',
		icon: 'defect',
		color: ratingColours[n],
		labelColor: ratingColours[n],
		zIndex: 1100 + n,
		properties: {
			rating: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

const v3ManholeSurveyLayers = (): ConfigLayer[] =>
	riskValues.map(n => ({
		layerName: `${n}`,
		id: `manhole-survey-${n}`,
		dataServiceId: 'tiles',
		type: 'symbol',
		icon: 'manhole-survey',
		color: ratingColours[n],
		lineWidth: 5,
		zIndex: 1000 + n,
		properties: {
			rating: [n - 0.5, n + 0.5],
		},
		selectable: false,
	}));

const inspectionLayers = (
	inspectionSources: SourceOptions[],
	noMinZoom = false,
	isTiledInspectionsEnabled = false,
): ConfigLayer[] => {
	const cctvSource = inspectionSources.find(
		n => n.dataServiceId === inspectionCctv,
	);
	const cctvObsSource = inspectionSources.find(
		n => n.dataServiceId === inspectionCctvObs,
	);
	const manholeSource = inspectionSources.find(
		n => n.dataServiceId === inspectionManhole,
	);

	const hasCctv = cctvSource || cctvObsSource;

	const cctvs = hasCctv
		? [
				{
					layerName: cctvSource?.layerName ?? '',
					layers: [
						cctvLayers(cctvSource, noMinZoom),
						{
							layerName: cctvObsSource?.layerName ?? '',
							layers: observationLayers(cctvObsSource, noMinZoom),
						},
					].flat(),
				},
		  ]
		: [];

	const tiledCctvs = [
		{
			layerName: 'CCTV',
			tileSource: 'tiled-inspections',
			tileLayerName: /[Ii]nspections/,
			layers: [
				tiledCctvLayers(),
				{
					layerName: 'Observations',
					tileSource: 'tiled-inspections',
					tileLayerName: /[Dd]efects/,
					layers: tiledObservationLayers(),
				},
			].flat(),
		},
	];

	const manholes = manholeSource
		? [
				{
					layerName: manholeSource?.layerName ?? '',
					layers: manholeSurveyLayers(manholeSource, noMinZoom),
				},
		  ]
		: [];

	const tiledManholes = [
		{
			layerName: 'Manhole surveys',
			tileSource: 'tiled-inspections',
			tileLayerName: /[Mm]anhole_[Ss]urveys/,
			layers: tiledManholeSurveyLayers(),
		},
	];

	const layers = [cctvs, manholes].flat();

	const tiledLayers = [tiledCctvs, tiledManholes].flat();

	if (isTiledInspectionsEnabled) {
		return tiledLayers.length > 0
			? [
					{
						layerName: 'Inspections',
						dataServiceId: 'tiled-inspections',
						layers: tiledLayers,
					},
			  ]
			: [];
	}

	return layers.length > 0
		? [
				{
					layerName: inspectionsLayerName,
					layers,
				},
		  ]
		: [];
};

const rehabLayers = (
	opts: SourceOptions | undefined,
	mapLayers: MapRehabLayer[] = [],
): ConfigLayer | undefined => {
	if (mapLayers.length < 1) {
		return;
	}

	return {
		layerName: 'Rehab',
		dataServiceId: opts?.dataServiceId ?? '',
		layers: mapLayers.map((l, treeIndex) => {
			const geometryType = getGeometryType(l.assetType);
			const layerConfig = getLayerConfigFromGeometryType(geometryType);
			const tileSourceRegex = createTileSourceRegExp(l.assetType);

			return {
				layerName: l.layerName,
				id: l.id,
				tileSource: tileSourceRegex,
				tileLayerName: l.assetType,
				zIndex: 100 + treeIndex,
				layers: l.layers?.map((layer, i) => ({
					...layer,
					...layerConfig,
					color: rehabColors[i],
					properties: {
						[`rehab${treeIndex}`]: layer.layerName,
					},
				})),
			};
		}),
	};
};

export const layerNameToId = (layerName: string): string =>
	layerName.replace(/[\s-]/g, '').toLowerCase();

export const mapGeometryType = (layer: SpatialLayer): MapType => {
	// SpatialLayer return CSV for all the GeoJson geometry types
	// in the imported FaetureCollection.
	// Hopfully these will all be of the same type, but that is
	// not actually guarentied. Hence the CSV

	// 2do refactor
	// saas_risk_management provides a GEOMETRY_TYPE called feature_type
	// in an interface confusingly also called SPATIAL_LAYER in app_am_asset_upload
	// Sadly this is actually only the value of the 1st feature in the FeatureCollection
	// so could be misleading, it is got from the SPATIAL_DATA snowflake table.
	// Currently the risk version is just used as a label in the
	// UI so just misleading rather than wrong. As mentioned earlier we cannot really
	// support mixed geometry types in a single FeatureCollection upload currently
	// (at least as far as the map is concerned). When
	// we do; we should also change the saas_risk_management response to use this method.

	// 'Like' as these pick up on 'Multi' versions as well
	const geometryType = layer.GEOMETRY_TYPE?.toLowerCase() ?? '';
	const isPointLike = geometryType.includes(PointType.toLowerCase());
	const isLineStringLike = geometryType.includes(
		LineStringType.toLowerCase(),
	);
	const isPolygonLike = geometryType.includes(PolygonType.toLowerCase());

	if (!isPolygonLike && !isLineStringLike && !isPointLike) {
		// Not specified, was layer ingested pre GEOMETRY_TYPE table
		console.warn(
			'mapGeometryType SPATIAL_LAYERS missing valid GEOMETRY_TYPE (guessed "symbol")',
		);
		console.warn(layer);
		// guessing symol as at least coords of line/polygon should render as symbols - so showing something
		return 'symbol';
	}

	if (isPolygonLike && !isLineStringLike && !isPointLike) {
		return 'polygon';
	}

	if (!isPolygonLike && isLineStringLike && !isPointLike) {
		return 'line';
	}

	if (!isPolygonLike && !isLineStringLike && isPointLike) {
		return 'symbol';
	}

	// Got a mixture, not supported - return 'highest' type
	console.warn('mapGeometryType contains mixture (highest used now)');
	console.warn(`  Polygon: ${isPolygonLike}`);
	console.warn(`  LineString: ${isLineStringLike}`);
	console.warn(`  Point: ${isPointLike}`);

	return isPolygonLike ? 'polygon' : 'line';
};

/**
 * Builds a minimal layer config for the single-inspection insert map.
 * Explicitly includes only:
 *   - The Sanitary Sewer tile layer (V2 or V3 depending on flag)
 *   - The inspection layers sourced by inspectionId (not broad V3 tile inspection layers)
 * This avoids the behaviour of configLayers() where isTilesV3Enabled replaces
 * per-inspection sources with V3 tile-wide inspection layers.
 */
// The tilesSource/otherSource on `options` carry the import-frozen legacy url
// (from tileOptions/otherOptions, resolved before feature flags existed). Swap
// in the flag-resolved host so the layer configs fetch tilesets from CloudOS
// when `i-360-tileset-read-from-cloudos` is on. No-op when the flag is off
// (getTilesBaseUrl(false) === tilesBaseUrl).
const resolveTilesHost = (
	options: MapOptions,
	isCloudOsTilesetEnabled: boolean,
): MapOptions => {
	const tilesUrl = getTilesBaseUrl(isCloudOsTilesetEnabled);
	return {
		...options,
		...(options.tilesSource && {
			tilesSource: { ...options.tilesSource, url: tilesUrl },
		}),
		...(options.otherSource && {
			otherSource: {
				...options.otherSource,
				url: options.otherSource.url.replace(tilesBaseUrl, tilesUrl),
			},
		}),
	};
};

export const configLayersInsertInspection = (
	token: string | undefined,
	options: MapOptions,
	isTilesV3Enabled = false,
	isCloudOsTilesetEnabled = false,
): ConfigLayer[] => {
	options = resolveTilesHost(options, isCloudOsTilesetEnabled);
	const layers: ConfigLayer[] = [];

	if (options.tilesSource && !options.tilesSourceHide) {
		const configCreator = isTilesV3Enabled
			? tilesConfigCreatorV3
			: tilesConfigCreator;

		layers.push(
			configCreator(
				{
					layerName: options.tilesSource.layerName,
					dataServiceId: options.tilesSource.dataServiceId,
					requestToken: token,
				},
				options.tilesSource.url,
			),
		);
	}

	if (options.inspectionSources && options.inspectionSources.length > 0) {
		inspectionLayers(
			options.inspectionSources,
			options.generalOptions.noMinZoom,
		).forEach(l => layers.push(l));
	}

	return layers;
};

export const configLayers = (
	token: string | undefined,
	options: MapOptions,
	spatialLayers: SpatialLayer[] = [],
	isTiledInspectionsEnabled = false,
	isTiledRiskEnabled = false,
	isTilesV3Enabled = false,
	isCloudOsTilesetEnabled = false,
): ConfigLayer[] => {
	options = resolveTilesHost(options, isCloudOsTilesetEnabled);
	const layers: ConfigLayer[] = [];

	if (options.tilesSource && !options.tilesSourceHide) {
		// Use V3 config creator when V3 is enabled to fetch from /v3/tileset-index
		const configCreator = isTilesV3Enabled
			? tilesConfigCreatorV3
			: tilesConfigCreator;

		layers.push(
			configCreator(
				{
					layerName: options.tilesSource.layerName,
					dataServiceId: options.tilesSource.dataServiceId,
					requestToken: token,
				},
				options.tilesSource.url,
			),
		);
	}

	if (options.rehabLayers) {
		const rehabLayerConfig = rehabLayers(
			options.tilesSource,
			options.rehabLayers,
		);
		if (rehabLayerConfig) {
			layers.push(rehabLayerConfig);
		}
	}

	if (options.showRisk) {
		riskLayers(
			options.tilesSource,
			options.riskLayers,
			isTiledRiskEnabled,
			isTilesV3Enabled,
		).forEach(l => layers.push(l));
	}

	// Add inspection layers
	if (isTilesV3Enabled) {
		// V3: Create inspection layers using V3 tile source
		const v3InspectionLayers = [
			{
				layerName: 'CCTV',
				tileSource: 'Inspections',
				tileLayerName: 'inspections',
				layers: [
					v3CctvLayers(),
					{
						layerName: 'Observations',
						tileSource: 'Inspections',
						tileLayerName: 'defects',
						layers: v3ObservationLayers(),
					},
				].flat(),
			},
			{
				layerName: 'Manhole surveys',
				tileSource: 'Inspections',
				tileLayerName: 'manhole_surveys',
				layers: v3ManholeSurveyLayers(),
			},
		];

		layers.push({
			layerName: 'Inspections',
			dataServiceId: 'tiles',
			layers: v3InspectionLayers,
		});
	} else if (
		options.inspectionSources &&
		options.inspectionSources.length > 0
	) {
		inspectionLayers(
			options.inspectionSources,
			options.generalOptions.noMinZoom,
			isTiledInspectionsEnabled,
		).forEach(l => layers.push(l));
	}

	// Add spatial/other layers
	if (isTilesV3Enabled && options.tilesSource) {
		// V3: Add spatial data layer config that uses AmSpatialConfigAdaptor
		// to fetch and process amSpatial from the V3 endpoint
		layers.push(
			amSpatialConfigCreatorV3(
				{
					layerName: 'Spatial Data',
					dataServiceId: 'tiles',
					tileSource: 'amSpatial',
					requestToken: token,
				},
				options.tilesSource.url,
			),
		);
	} else if (options.otherSource) {
		if (
			spatialLayers.length &&
			(options?.useSpecifiedSpatialLayer ?? false)
		) {
			spatialLayers.forEach(l =>
				layers.push({
					id: layerNameToId(l.LAYER_NAME),
					layerName: l.LAYER_NAME,
					tileSource:
						options.otherSource?.dataServiceId ?? 'UNDEFINED',
					tileLayerName: layerNameToId(l.LAYER_NAME),
					dataServiceId:
						options.otherSource?.dataServiceId ?? 'UNDEFINED',
					type: mapGeometryType(l),
				}),
			);
		} else {
			layers.push(
				tilesJsonConfigCreator(
					{
						layerName: options.otherSource.layerName,
						dataServiceId: options.otherSource.dataServiceId,
						tileSource: options.otherSource.dataServiceId,
						requestToken: token,
					},
					options.otherSource.url,
				),
			);
		}
	}

	return layers;
};
